import { useState, useEffect } from 'react';
import {
  AreaChart, Area, BarChart, Bar, ComposedChart, Line, LineChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { X, ArrowUpRight, ArrowDownRight, ExternalLink, BarChart3 } from 'lucide-react';
import { getAllSecurities } from '../data/dynamicSecurities';
import {
  getQuote, getHistory, getIntraday, getTechnicals, computePlProbability,
} from '../utils/marketEngine';
import { getCompanyNews, getCompanyProfile } from '../utils/polygonService';
import { TERMS } from '../utils/terms';

const tooltipStyle = {
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-sm)',
  fontSize: 12,
  color: 'var(--text-primary)',
};

const displayTicker = (t) => t || '';

export default function SecurityDetailModal({ ticker, onClose }) {
  const sec = getAllSecurities().find((s) => s.ticker === ticker);
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [intraday, setIntraday] = useState([]);
  const [plProb, setPlProb] = useState([]);
  const [technicals, setTechnicals] = useState(null);
  const [liveData, setLiveData] = useState({ price: 0, prevClose: 0, volume: 0, change: 0, changePct: 0, name: null, exchange: null });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('price');
  const [timeRange, setTimeRange] = useState('market'); // 'market' | '6H' | '12H' | '24H'
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const cs = '$';

  // NYSE/NASDAQ session times in exchange-local timezone.
  const tzName    = 'America/New_York';
  const tzLabel   = 'ET';
  const mktOpen   = { h: 9, m: 30 };
  const mktClose  = { h: 16, m: 0 };

  function isInMarketHours(tsMs) {
    const d = new Date(tsMs);
    // Get hours/minutes in the exchange's timezone
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tzName, hour: 'numeric', minute: 'numeric', hour12: false,
    }).formatToParts(d);
    const h = parseInt(parts.find(p => p.type === 'hour').value);
    const m = parseInt(parts.find(p => p.type === 'minute').value);
    const totalMin = h * 60 + m;
    return totalMin >= mktOpen.h * 60 + mktOpen.m && totalMin <= mktClose.h * 60 + mktClose.m;
  }

  function isTodayInExchangeTz(tsMs) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tzName, year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const todayStr = formatter.format(new Date());
    return formatter.format(new Date(tsMs)) === todayStr;
  }

  function fmtBarTime(tsMs) {
    return new Date(tsMs).toLocaleTimeString('en-US', {
      timeZone: tzName, hour: '2-digit', minute: '2-digit', hour12: true,
    });
  }

  function filterIntraday(data) {
    const now = Date.now();
    if (timeRange === 'market') {
      return data.filter(d => isTodayInExchangeTz(d.timestamp) && isInMarketHours(d.timestamp));
    }
    const hours = timeRange === '6H' ? 6 : timeRange === '12H' ? 12 : 24;
    const cutoff = now - hours * 60 * 60 * 1000;
    return data.filter(d => d.timestamp >= cutoff);
  }

  // Load data on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [hist, quote, tech, prof] = await Promise.all([
          getHistory(ticker),
          getQuote(ticker),
          getTechnicals(ticker).catch(() => null),
          !sec ? getCompanyProfile(ticker).catch(() => null) : Promise.resolve(null),
        ]);
        if (prof) setProfile(prof);
        if (cancelled) return;
        setHistory(hist || []);
        if (tech) setTechnicals(tech);
        if (quote) {
          setLiveData({
            price: quote.price, prevClose: quote.prevClose || quote.price,
            high: quote.high, low: quote.low, open: quote.open,
            volume: quote.volume || 0, change: quote.change || 0, changePct: quote.changePct || 0,
            name: quote.name || null, exchange: quote.exchange || null,
          });
          // Use real IV from technicals when available; RSI-based drift shifts distribution
          const iv = tech?.impliedVolatility || tech?.historicalVolatility || 30;
          const rsiDrift = tech?.rsi != null ? (50 - tech.rsi) * 0.0004 : 0; // RSI<50 → slight negative drift
          setPlProb(computePlProbability(quote.price, iv, 5, rsiDrift));
        }
        // Load intraday separately (may take longer)
        getIntraday(ticker).then(d => { if (!cancelled && d) setIntraday(d); }).catch(() => {});
      } catch (e) {
        console.error('Failed to load security data:', e);
      }
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [ticker]);

  // Lazy-load news when News tab is selected
  useEffect(() => {
    if (tab !== 'news') return;
    if (news.length > 0) return; // already loaded
    let cancelled = false;
    setNewsLoading(true);
    getCompanyNews(ticker).then(articles => {
      if (!cancelled) { setNews(articles); setNewsLoading(false); }
    }).catch(() => { if (!cancelled) setNewsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, ticker]);

  // Refresh quote every 15 seconds
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const q = await getQuote(ticker);
        if (q) setLiveData(prev => ({ ...prev, price: q.price, change: q.change, changePct: q.changePct, high: q.high, low: q.low }));
      } catch (e) { /* silent */ }
    }, 15000);
    return () => clearInterval(iv);
  }, [ticker]);

  const change = liveData.change || (liveData.price - (liveData.prevClose || liveData.price));
  const changePct = liveData.changePct || (liveData.prevClose ? ((liveData.price - liveData.prevClose) / liveData.prevClose) * 100 : 0);
  const up = change >= 0;

  const tabs = [
    { key: 'price', label: 'Price Chart' },
    { key: 'intraday', label: 'Today' },
    { key: 'volume', label: 'Volume' },
    { key: 'pl', label: 'P&L Probability' },
    { key: 'technicals', label: 'Technicals' },
    { key: 'news', label: 'News' },
  ];

  const fmtVal = (v, dec = 2, prefix = '') => v != null && !isNaN(v) ? `${prefix}${Number(v).toFixed(dec)}` : '—';
  const fmtVol = (v) => v != null && !isNaN(v) && v > 0 ? `${(v / 1e6).toFixed(1)}M` : '—';

  // Derive bandwidth and price vs mid band from the fetched BB values
  const bbBandwidth = (technicals?.bollingerUpper != null && technicals?.bollingerLower != null && technicals?.bollingerMid != null && technicals.bollingerMid !== 0)
    ? ((technicals.bollingerUpper - technicals.bollingerLower) / technicals.bollingerMid * 100)
    : null;
  const priceVsMid = (liveData.price && technicals?.bollingerMid)
    ? ((liveData.price - technicals.bollingerMid) / technicals.bollingerMid * 100)
    : null;

  // Indicators sourced from the server-computed Polygon technicals:
  // RSI, MACD (line + signal + histogram), Bollinger Bands (upper + mid + lower),
  // BB Bandwidth and Price vs Mid Band (derived), and live Volume.
  const technicalItems = technicals ? [
    { label: 'RSI (14)',        value: fmtVal(technicals.rsi, 1),          color: (technicals.rsi || 50) < 30 ? '#10b981' : (technicals.rsi || 50) > 70 ? '#ef4444' : '#f59e0b', tip: TERMS.rsi },
    { label: 'MACD Line',      value: fmtVal(technicals.macd, 3),          color: (technicals.macd || 0) > 0 ? '#10b981' : '#ef4444',                                       tip: TERMS.macd },
    { label: 'MACD Signal',    value: fmtVal(technicals.macdSignal, 3),    color: '#94a3b8',                                                                                tip: '9-period EMA of MACD Line. Cross of MACD Line above/below the signal is the classic trade trigger.' },
    { label: 'MACD Histogram', value: fmtVal(technicals.macdHist, 3),      color: (technicals.macdHist || 0) > 0 ? '#10b981' : '#ef4444',                                  tip: 'MACD Line − Signal. Histogram switching from negative to positive (or vice versa) precedes the cross.' },
    { label: 'BB Upper',       value: fmtVal(technicals.bollingerUpper, 2, cs), color: '#f59e0b',                                                                           tip: TERMS.bollinger + ' Upper band = 20-SMA + 2 standard deviations.' },
    { label: 'BB Mid (SMA 20)',value: fmtVal(technicals.bollingerMid, 2, cs),   color: '#6366f1',                                                                           tip: '20-day Simple Moving Average — the centre of the Bollinger envelope. Price reverts here a lot.' },
    { label: 'BB Lower',       value: fmtVal(technicals.bollingerLower, 2, cs), color: '#f59e0b',                                                                           tip: '20-SMA − 2 standard deviations. Price tagging this band is a stretched-down read; combine with RSI for a bounce setup.' },
    { label: 'BB Width',       value: fmtVal(bbBandwidth, 1) + '%',        color: '#f59e0b',                                                                                tip: '(Upper − Lower) / Mid. Low width = consolidation (squeeze, often before a big move); high width = active trend or post-event vol.' },
    { label: 'Price vs Mid',   value: (priceVsMid != null ? (priceVsMid >= 0 ? '+' : '') + priceVsMid.toFixed(1) + '%' : '—'), color: priceVsMid != null && priceVsMid >= 0 ? '#10b981' : '#ef4444', tip: 'Where current price sits relative to the 20-SMA. Positive = above mean (potentially extended); negative = below (potentially oversold).' },
    { label: 'Volume',         value: fmtVol(liveData.volume),             color: '#0ea5e9',                                                                                tip: 'Shares traded today. Volume spikes (>2× average) often confirm the move; low volume on a breakout is suspicious.' },
  ] : [];

  return (
    <div
      className="animate-fade-in"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, display: 'flex',
        background: 'var(--bg-overlay)', backdropFilter: 'blur(10px)',
      }}
      onClick={onClose}
    >
      <div
        className="animate-slide-up glass"
        style={{
          margin: 'auto', width: '95%', maxWidth: 940, maxHeight: '95vh',
          overflow: 'auto',
          borderRadius: 'var(--r-xl)',
          border: '1px solid rgba(91,106,240,0.20)',
          padding: '28px 28px 24px',
          boxShadow: 'var(--shadow-modal)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
            {/* Gradient icon tile */}
            <div style={{
              width: 48, height: 48, borderRadius: 'var(--r-md)', flexShrink: 0,
              background: 'var(--grad-accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(91,106,240,0.32)',
            }}>
              <BarChart3 size={22} color="#fff" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span className="mono" style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>
                  {displayTicker(ticker)}
                </span>
                {(sec?.category || profile?.exchange || liveData.exchange) && (
                  <span style={{
                    fontSize: 11, padding: '3px 10px', borderRadius: 'var(--r-pill)', fontWeight: 600, letterSpacing: '0.3px',
                    background: (sec?.categoryColor || 'var(--accent)') + '20',
                    color: sec?.categoryColor || 'var(--accent-soft)',
                    border: `1px solid ${(sec?.categoryColor || 'var(--accent)')}30`,
                  }}>
                    {sec?.category || profile?.exchange || liveData.exchange}
                  </span>
                )}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                {(() => {
                  const name = sec?.name || profile?.name || liveData.name;
                  const detail = sec?.sector || profile?.industry || null;
                  if (name && detail) return `${name} · ${detail}`;
                  return name || detail || '';
                })()}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div className="modal-price-block" style={{ textAlign: 'right' }}>
              <div className="mono" style={{ fontSize: 30, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>
                {cs}{liveData.price?.toFixed(2)}
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 10px', borderRadius: 'var(--r-pill)', marginTop: 4,
                background: up ? 'var(--green-dim)' : 'var(--red-dim)',
                border: `1px solid ${up ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
              }}>
                {up ? <ArrowUpRight size={14} color="var(--green)" /> : <ArrowDownRight size={14} color="var(--red)" />}
                <span className="mono" style={{ color: up ? 'var(--green)' : 'var(--red)', fontWeight: 600, fontSize: 12 }}>
                  {up ? '+' : ''}{change.toFixed(2)} ({changePct.toFixed(2)}%)
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: 36, height: 36, borderRadius: 'var(--r-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', transition: `all var(--dur-fast) var(--ease-out)`, flexShrink: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--red-dim)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.30)'; e.currentTarget.style.color = 'var(--red-soft)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-elevated)'; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Tabs — pill geometry */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
          {tabs.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  padding: '8px 14px', borderRadius: 'var(--r-pill)',
                  cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  fontFamily: 'inherit', transition: `all var(--dur-fast) var(--ease-out)`,
                  background: active ? 'var(--grad-accent)' : 'var(--bg-elevated)',
                  color: active ? '#fff' : 'var(--text-secondary)',
                  border: active ? '1px solid transparent' : '1px solid var(--border)',
                  boxShadow: active ? '0 4px 14px rgba(91,106,240,0.32)' : 'none',
                }}
                onMouseEnter={(e) => { if (!active) { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; } }}
                onMouseLeave={(e) => { if (!active) { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border)'; } }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Chart panel */}
        <div
          style={{
            background: 'var(--bg-card)', borderRadius: 'var(--r-lg)',
            padding: 20, border: '1px solid var(--border-subtle)',
          }}
        >
          {tab === 'price' && history.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>No price history available for {displayTicker(ticker)}</div>
          )}
          {tab === 'price' && history.length > 0 && (() => {
            // Candlestick-style: line chart with OHLC data + volume bars below
            const chartData = history.slice(-60).map(d => ({
              ...d,
              color: d.close >= d.open ? 'var(--green)' : 'var(--red)',
              volColor: d.close >= d.open ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)',
            }));
            const refPrice = liveData.price || (chartData.length > 0 ? chartData[chartData.length - 1]?.close : 0);
            return (
            <div>
              {/* Price line */}
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={chartData} margin={{ top: 5, right: 45, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,41,59,0.3)" />
                  <XAxis dataKey="date" axisLine={{ stroke: 'var(--border)' }} tickLine={false}
                    tick={{ fontSize: 9, fill: 'var(--text-muted)' }}
                    tickFormatter={v => { const d = new Date(v); return `${d.getMonth()+1}/${d.getDate()}`; }}
                    interval={Math.max(1, Math.floor(chartData.length / 8))} />
                  <YAxis axisLine={{ stroke: 'var(--border)' }} tickLine={false}
                    tick={{ fontSize: 9, fill: 'var(--text-muted)' }} domain={['auto','auto']} width={45}
                    tickFormatter={v => v.toFixed(0)} />
                  <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--text-primary)' }}
                    labelFormatter={v => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    formatter={(v,name) => [`${cs}${Number(v).toFixed(2)}`, name==='close'?'Close':name==='high'?'High':name==='low'?'Low':'Open']} />
                  <ReferenceLine y={refPrice} stroke="var(--accent)" strokeDasharray="4 4" strokeOpacity={0.4}
                    label={{ value: refPrice?.toFixed(2), position: 'right', fill: 'var(--accent)', fontSize: 10 }} />
                  <Line type="monotone" dataKey="close" stroke="#4a5899" strokeWidth={1.5} dot={false}
                    activeDot={{ r: 3, fill: 'var(--accent)', stroke: 'var(--bg-card)', strokeWidth: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
              {/* Volume bars underneath */}
              <ResponsiveContainer width="100%" height={60}>
                <BarChart data={chartData} margin={{ top: 0, right: 45, left: 0, bottom: 0 }}>
                  <XAxis dataKey="date" hide />
                  <YAxis hide />
                  <Bar dataKey="volume" radius={[2,2,0,0]}>
                    {chartData.map((d, i) => (
                      <Cell key={i} fill={d.close >= d.open ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            );
          })()}

          {tab === 'intraday' && (() => {
            const filtered = filterIntraday(intraday);
            const idData   = filtered.map(d => ({ ...d, time: fmtBarTime(d.timestamp) }));
            const ranges   = [
              { key: 'market', label: `Market Hours (${tzLabel})` },
              { key: '6H',     label: 'Last 6H' },
              { key: '12H',    label: 'Last 12H' },
              { key: '24H',    label: 'Last 24H' },
            ];
            return (
              <div>
                {/* Time range selector */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  {ranges.map(r => {
                    const active = timeRange === r.key;
                    return (
                      <button key={r.key} onClick={() => setTimeRange(r.key)} style={{
                        padding: '5px 12px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
                        fontSize: 10, fontWeight: 600, fontFamily: 'inherit',
                        transition: `all var(--dur-fast) var(--ease-out)`,
                        background: active ? 'var(--grad-accent)' : 'var(--bg-elevated)',
                        color: active ? '#fff' : 'var(--text-secondary)',
                        border: active ? '1px solid transparent' : '1px solid var(--border)',
                        boxShadow: active ? '0 2px 8px rgba(91,106,240,0.28)' : 'none',
                      }}>{r.label}</button>
                    );
                  })}
                  <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 4 }}>
                    Times shown in {tzLabel}
                  </span>
                </div>

                {idData.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                    {timeRange === 'market' ? `No data for today's market session yet` : `No data for this time window`}
                  </div>
                ) : (
                  <div>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={idData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,41,59,0.3)" />
                        <XAxis dataKey="time" axisLine={{ stroke: 'var(--border)' }} tickLine={false}
                          tick={{ fontSize: 9, fill: 'var(--text-muted)' }}
                          interval={Math.max(1, Math.floor(idData.length / 8))} />
                        <YAxis axisLine={{ stroke: 'var(--border)' }} tickLine={false}
                          tick={{ fontSize: 9, fill: 'var(--text-muted)' }} domain={['auto', 'auto']}
                          tickFormatter={v => `${cs}${v.toFixed(2)}`} />
                        <Tooltip
                          contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--text-primary)' }}
                          formatter={(v) => [`${cs}${Number(v).toFixed(2)}`, 'Price']}
                          labelFormatter={label => `${label} ${tzLabel}`}
                        />
                        <Line type="monotone" dataKey="price" stroke="#4a5899" strokeWidth={1.5} dot={false}
                          activeDot={{ r: 3, fill: 'var(--accent)', stroke: 'var(--bg-card)', strokeWidth: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                    <ResponsiveContainer width="100%" height={50}>
                      <BarChart data={idData} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                        <XAxis dataKey="time" hide />
                        <YAxis hide />
                        <Bar dataKey="volume" fill="rgba(91,106,240,0.15)" radius={[1, 1, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            );
          })()}

          {tab === 'volume' && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={history.slice(-30)} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,41,59,0.3)" />
                <XAxis dataKey="date" axisLine={{ stroke: 'var(--border)' }} tickLine={false}
                  tick={{ fontSize: 9, fill: 'var(--text-muted)' }}
                  tickFormatter={v => { const d = new Date(v); return `${d.getMonth()+1}/${d.getDate()}`; }} />
                <YAxis axisLine={{ stroke: 'var(--border)' }} tickLine={false}
                  tick={{ fontSize: 9, fill: 'var(--text-muted)' }}
                  tickFormatter={v => `${(v/1e6).toFixed(0)}M`} />
                <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--text-primary)' }}
                  formatter={v => [`${(v/1e6).toFixed(1)}M`, 'Volume']} />
                <Bar dataKey="volume" radius={[3,3,0,0]}>
                  {history.slice(-30).map((d, i) => (
                    <Cell key={i} fill={d.close >= d.open ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}

          {tab === 'pl' && (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600, marginBottom: 4 }}>
                Profit & Loss Probability — Next 5 Trading Days
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>
                Based on implied volatility. Shows the probability of each return outcome per share.
              </div>

              {/* Main probability curve */}
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={plProb} margin={{ top: 10, right: 20, bottom: 20, left: 20 }}>
                  <defs>
                    <linearGradient id="gplGreen" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gplRed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gplPurple" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.3)" />
                  <XAxis
                    dataKey="returnPct"
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}%`}
                    label={{ value: '← Loss        Return per Share        Profit →', position: 'bottom', offset: 0, style: { fontSize: 11, fill: '#64748b' } }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                    label={{ value: 'Probability', angle: -90, position: 'insideLeft', offset: -5, style: { fontSize: 11, fill: '#64748b' } }}
                  />
                  <Tooltip
                    contentStyle={{ ...tooltipStyle, padding: '10px 14px' }}
                    labelFormatter={(v) => `Return: ${v > 0 ? '+' : ''}${v}%`}
                    formatter={(v, name) => {
                      if (name === 'probability') return [`${(v * 100).toFixed(2)}% chance`, 'Probability'];
                      return [v, name];
                    }}
                  />
                  {/* Reference line at 0% (breakeven) */}
                  <Area type="monotone" dataKey="probability" stroke="#8b5cf6" fill="url(#gplPurple)" strokeWidth={2.5} />
                </AreaChart>
              </ResponsiveContainer>

              {/* Summary cards */}
              <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                {(() => {
                  // Find cumulative probability at breakeven (returnPct closest to 0)
                  const bePoint = plProb.length
                    ? plProb.reduce((best, p) => Math.abs(p.returnPct) < Math.abs(best.returnPct) ? p : best, plProb[0])
                    : null;
                  const probProfit = bePoint ? ((1 - bePoint.cumProb) * 100).toFixed(0) : '50';

                  const price = liveData.price || 100;
                  const iv = technicals?.impliedVolatility || technicals?.historicalVolatility || 30;
                  const expectedMove = (iv / 100 / Math.sqrt(252) * Math.sqrt(5) * price).toFixed(2);
                  const expectedMovePct = (iv / 100 / Math.sqrt(252) * Math.sqrt(5) * 100).toFixed(1);

                  // 90th percentile gain and 10th percentile loss from actual distribution
                  const p90 = plProb.find(p => p.cumProb >= 0.9);
                  const p10 = plProb.find(p => p.cumProb >= 0.1);
                  const gain90 = p90 ? Math.abs(p90.returnPct).toFixed(1) : '5.0';
                  const loss10 = p10 ? Math.abs(p10.returnPct).toFixed(1) : '5.0';
                  const gain90Abs = p90 ? Math.abs(price * p90.returnPct / 100).toFixed(2) : (price * 0.05).toFixed(2);
                  const loss10Abs = p10 ? Math.abs(price * p10.returnPct / 100).toFixed(2) : (price * 0.05).toFixed(2);

                  return [
                    { label: 'Probability of Profit', value: `${probProfit}%`, sub: 'chance of positive return', color: parseInt(probProfit) > 50 ? 'var(--green)' : 'var(--red)', tint: parseInt(probProfit) > 50 ? 'var(--green-dim)' : 'var(--red-dim)' },
                    { label: 'Expected 5-Day Move', value: `±${cs}${expectedMove}`, sub: `±${expectedMovePct}% per share`, color: 'var(--amber)', tint: 'var(--amber-dim)' },
                    { label: 'Max Likely Gain (90th %ile)', value: `+${cs}${gain90Abs}`, sub: `+${gain90}% per share`, color: 'var(--green)', tint: 'var(--green-dim)' },
                    { label: 'Max Likely Loss (10th %ile)', value: `-${cs}${loss10Abs}`, sub: `-${loss10}% per share`, color: 'var(--red)', tint: 'var(--red-dim)' },
                  ].map(m => (
                    <div key={m.label} style={{
                      flex: '1 1 0', minWidth: 160, background: m.tint, borderRadius: 'var(--r-md)',
                      padding: '12px 14px', border: `1px solid ${m.color}30`,
                    }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600, letterSpacing: '0.3px' }}>{m.label}</div>
                      <div className="mono" style={{ fontSize: 20, fontWeight: 700, color: m.color }}>{m.value}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-faint)', marginTop: 2 }}>{m.sub}</div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {tab === 'technicals' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12 }}>
              {technicalItems.map((t) => (
                <div
                  key={t.label}
                  title={t.tip}
                  style={{
                    background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)',
                    padding: '12px 14px', border: '1px solid var(--border-subtle)',
                    transition: `all var(--dur-fast) var(--ease-out)`,
                    cursor: t.tip ? 'help' : 'default',
                  }}
                >
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600, letterSpacing: '0.3px' }}>
                    {t.label}
                  </div>
                  <div className="mono" style={{ fontSize: 18, fontWeight: 700, color: t.color }}>
                    {t.value}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'news' && (
            <div>
              {newsLoading && (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Loading news...</div>
              )}
              {!newsLoading && news.length === 0 && (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>No recent news found for {displayTicker(ticker)}</div>
              )}
              {!newsLoading && news.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {news.map((article, i) => {
                    const sentColor = article.sentiment === 'positive' ? 'var(--green)' : article.sentiment === 'negative' ? 'var(--red)' : 'var(--amber)';
                    const sentBg    = article.sentiment === 'positive' ? 'var(--green-dim)' : article.sentiment === 'negative' ? 'var(--red-dim)' : 'var(--amber-dim)';
                    const sentBorder = article.sentiment === 'positive' ? 'rgba(34,197,94,0.25)' : article.sentiment === 'negative' ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.25)';
                    const sentLabel = article.sentiment === 'positive' ? '↑ Bullish' : article.sentiment === 'negative' ? '↓ Bearish' : '→ Neutral';
                    const dateStr   = article.datetime ? new Date(article.datetime * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                    return (
                      <div key={i} style={{
                        background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)',
                        padding: '14px 16px', border: '1px solid var(--border-subtle)',
                        transition: `all var(--dur-fast) var(--ease-out)`,
                      }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                          <a href={article.url} target="_blank" rel="noreferrer"
                            style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, lineHeight: 1.4, textDecoration: 'none', flex: 1, transition: `color var(--dur-fast) var(--ease-out)` }}
                            onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-soft)'}
                            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-primary)'}
                          >
                            {article.headline}
                            <ExternalLink size={11} style={{ marginLeft: 5, verticalAlign: 'middle', opacity: 0.5 }} />
                          </a>
                          <span style={{
                            fontSize: 10, padding: '2px 8px', borderRadius: 'var(--r-pill)',
                            fontWeight: 600, whiteSpace: 'nowrap',
                            background: sentBg, color: sentColor, border: `1px solid ${sentBorder}`,
                          }}>
                            {sentLabel}
                          </span>
                        </div>
                        {article.summary && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 6 }}>
                            {article.summary.length > 200 ? article.summary.slice(0, 200) + '…' : article.summary}
                          </div>
                        )}
                        <div style={{ fontSize: 10, color: 'var(--text-faint)', display: 'flex', gap: 8 }}>
                          <span>{article.source}</span>
                          {dateStr && <span>· {dateStr}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
